import React from "react";
import {
  Typography,
  Paper,
  Box,
  Alert,
  Tooltip,
  IconButton,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import AppsIcon from "@mui/icons-material/Apps";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BalanceIcon from "@mui/icons-material/Balance";
import color from "../../Constants/colors";
import Loading from "../Common/Loading";
import { useDbConnections } from "../../Query/useMonitoring";
import { DbConnectionEntry } from "../../Api/monitoring";
import { useQueryClient } from "@tanstack/react-query";
import { PieChart } from "@mui/x-charts/PieChart";
import { getHealthInfo } from "../../Helpers/gradeHelpers";
import { useTranslation } from "react-i18next";
import { MONITORING_PIE_COLORS } from "../../Constants/monitoringColors";

const MAX_CONNECTIONS = 100;

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactElement;
  accentColor: string;
}> = ({ title, value, icon, accentColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: "14px",
      flex: "1 1 200px",
      maxWidth: 260,
      minWidth: 180,
      backgroundColor: color.primary,
      border: `1px solid ${color.gray200}`,
      position: "relative",
      overflow: "hidden",
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": {
        boxShadow: "0 4px 20px rgba(5,77,127,0.10)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        backgroundColor: accentColor,
        borderRadius: "14px 14px 0 0",
      }}
    />
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: `${accentColor}14`,
          color: accentColor,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: color.gray500,
        }}
      >
        {title}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 800,
        fontSize: "2rem",
        lineHeight: 1.1,
        color: color.textPrimary,
      }}
    >
      {value}
    </Typography>
  </Paper>
);

const ConnectionMeter: React.FC<{ totalConnections: number }> = ({
  totalConnections,
}) => {
  const percentage = Math.min(
    100,
    Math.round((totalConnections / MAX_CONNECTIONS) * 100),
  );
  const health = getHealthInfo(percentage);
  const { t } = useTranslation("monitoring");

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "14px",
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            color: color.textPrimary,
          }}
        >
          {t("connectionUsage")}
        </Typography>
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            borderRadius: "20px",
            backgroundColor: health.bgColor,
            border: `1px solid ${health.color}30`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: health.color,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {health.label}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ position: "relative", mb: 1.5 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 28,
            borderRadius: "8px",
            backgroundColor: color.gray100,
            "& .MuiLinearProgress-bar": {
              borderRadius: "8px",
              backgroundColor: health.barColor,
              transition: "transform 0.6s ease",
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.8rem",
              fontWeight: 800,
              color: percentage > 45 ? "#fff" : color.textPrimary,
              textShadow:
                percentage > 45 ? "0 1px 2px rgba(0,0,0,0.2)" : "none",
            }}
          >
            {totalConnections} / {MAX_CONNECTIONS}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", px: 0.5 }}>
        {[0, 25, 50, 75, 100].map((mark) => (
          <Typography
            key={mark}
            sx={{
              fontSize: "0.625rem",
              color: color.gray500,
              fontWeight: 500,
            }}
          >
            {mark}
          </Typography>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mt: 2,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {[
          { range: "0-50", label: t("healthy"), c: color.green700 },
          { range: "50-75", label: t("moderate"), c: color.yellow700 },
          { range: "75-90", label: t("atRisk"), c: color.orange600 },
          { range: "90-100", label: t("critical"), c: color.red600 },
        ].map((item) => (
          <Box
            key={item.range}
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                backgroundColor: item.c,
              }}
            />
            <Typography sx={{ fontSize: "0.65rem", color: color.gray500 }}>
              {item.range} {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const SharePieChart: React.FC<{
  connections: DbConnectionEntry[];
  total: number;
}> = ({ connections, total }) => {
  const pieData = connections.map((entry, idx) => ({
    id: idx,
    value: entry.connection_count,
    label: entry.application_name,
    color: MONITORING_PIE_COLORS[idx % MONITORING_PIE_COLORS.length],
  }));
  const { t } = useTranslation("monitoring");

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "14px",
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography
        sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 700,
          fontSize: "1rem",
          color: color.textPrimary,
          mb: 2,
          alignSelf: "flex-start",
        }}
      >
        {t("connectionShare")}
      </Typography>
      <Box sx={{ width: "100%", maxWidth: 420, height: 280 }}>
        <PieChart
          series={[
            {
              data: pieData,
              innerRadius: 50,
              outerRadius: 110,
              paddingAngle: 2,
              cornerRadius: 4,
              highlightScope: { faded: "global", highlighted: "item" },
              valueFormatter: (item) => {
                const pct =
                  total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
                return `${item.value} (${pct}%)`;
              },
            },
          ]}
          height={280}
          slotProps={{
            legend: {
              direction: "column",
              position: { vertical: "middle", horizontal: "right" },
              itemMarkWidth: 12,
              itemMarkHeight: 12,
              markGap: 5,
              itemGap: 8,
              labelStyle: {
                fontSize: 12,
                fontWeight: 600,
                fill: color.textPrimary as string,
              },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

const ConnectionsTable: React.FC<{
  connections: DbConnectionEntry[];
  total: number;
}> = ({ connections, total }) => {
  const sorted = [...connections].sort(
    (a, b) => b.connection_count - a.connection_count,
  );
  const { t } = useTranslation("monitoring");

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "14px",
        backgroundColor: color.primary,
        border: `1px solid ${color.gray200}`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 3, pb: 0 }}>
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "1rem",
            color: color.textPrimary,
          }}
        >
          {t("connectionsByApplication")}
        </Typography>
      </Box>
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: color.secondary,
              }}
            >
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  border: "none",
                  py: 1.5,
                  pl: 3,
                }}
              >
                {t("application")}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  border: "none",
                  py: 1.5,
                }}
              >
                {t("connections")}
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  border: "none",
                  py: 1.5,
                }}
              >
                {t("share")}
              </TableCell>
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  border: "none",
                  py: 1.5,
                  pr: 3,
                }}
              >
                {t("usageBar")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((entry, idx) => {
              const pct =
                total > 0
                  ? ((entry.connection_count / total) * 100).toFixed(1)
                  : "0";
              const barWidth =
                total > 0 ? (entry.connection_count / total) * 100 : 0;

              return (
                <TableRow
                  key={entry.application_name}
                  sx={{
                    backgroundColor:
                      idx % 2 === 0 ? color.primary : color.gray50,
                    "&:hover": {
                      backgroundColor: color.blue50,
                    },
                    transition: "background-color 0.15s",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8125rem",
                      color: color.textPrimary,
                      border: "none",
                      py: 1.5,
                      pl: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            MONITORING_PIE_COLORS[
                              idx % MONITORING_PIE_COLORS.length
                            ],
                          flexShrink: 0,
                        }}
                      />
                      {entry.application_name}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: color.secondary,
                      border: "none",
                      py: 1.5,
                    }}
                  >
                    {entry.connection_count}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: color.gray500,
                      border: "none",
                      py: 1.5,
                    }}
                  >
                    {pct}%
                  </TableCell>
                  <TableCell
                    sx={{
                      border: "none",
                      py: 1.5,
                      pr: 3,
                      minWidth: 160,
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 10,
                        backgroundColor: color.gray100,
                        borderRadius: "5px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${barWidth}%`,
                          height: "100%",
                          backgroundColor:
                            MONITORING_PIE_COLORS[
                              idx % MONITORING_PIE_COLORS.length
                            ],
                          borderRadius: "5px",
                          transition: "width 0.5s ease",
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const DatabaseMonitoring: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt } = useDbConnections();
  const queryClient = useQueryClient();
  const { t } = useTranslation("monitoring");

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={color.accent}
        color2={color.secondary}
      />
    );
  }

  if (isError) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">
          {t("failedToLoad", {
            message: (error as Error)?.message || t("unknownError"),
          })}
        </Alert>
      </Container>
    );
  }

  if (!data) return null;

  const connections = data.db_connections;
  const totalConnections = connections.reduce(
    (sum, c) => sum + c.connection_count,
    0,
  );
  const activeApps = connections.length;
  const peakUsage =
    connections.length > 0
      ? Math.max(...connections.map((c) => c.connection_count))
      : 0;
  const avgPerApp =
    activeApps > 0 ? (totalConnections / activeApps).toFixed(1) : "0";

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: color.primary,
        py: 4,
        px: 2,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 700,
              fontSize: "1.75rem",
              color: color.secondary,
              mb: 0.5,
            }}
          >
            {t("title")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "0.75rem", color: color.gray500 }}>
              {t("autoRefreshWithLastUpdated", { time: lastUpdated })}
            </Typography>
            <Tooltip title={t("refreshNow")}>
              <IconButton
                size="small"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ["dbConnections"],
                  })
                }
                sx={{
                  color: color.secondary,
                  "&:hover": { color: color.accent },
                }}
              >
                <RefreshIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2.5,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 4,
          }}
        >
          <StatCard
            title={t("totalConnections")}
            value={totalConnections}
            icon={<StorageIcon />}
            accentColor={color.secondary}
          />
          <StatCard
            title={t("activeApplications")}
            value={activeApps}
            icon={<AppsIcon />}
            accentColor={color.accent}
          />
          <StatCard
            title={t("peakUsage")}
            value={peakUsage}
            icon={<TrendingUpIcon />}
            accentColor={color.green700}
          />
          <StatCard
            title={t("avgPerApp")}
            value={avgPerApp}
            icon={<BalanceIcon />}
            accentColor={color.blue700}
          />
        </Box>
        <Box sx={{ mb: 4 }}>
          <ConnectionMeter totalConnections={totalConnections} />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            mb: 4,
          }}
        >
          <SharePieChart connections={connections} total={totalConnections} />
          <ConnectionsTable
            connections={connections}
            total={totalConnections}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default DatabaseMonitoring;
